import { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { formatDateTime } from '@/lib/date-display.js';
import useMe from '@/hooks/useMe.js';
import { ownerPagesVisible } from '@/lib/session.js';
import {
  getProducerShares, setProducerShares, setDefaultProducer, percentToShare, shareToPercent, shareWords,
} from '@/services/producer-shares.service.js';

// PRODUCER SHARES (deals-door-1 answers round 3, R2c-Q1 and R2c-Q4). Each producer's stated share of the agency's commission,
// new business and renewal separately; a policy may override it on the deal (the producer key). Blank is "share not stated":
// the commission view then shows no producer amount, never 100%. The default producer is who a new deal gets when no producer
// is stated. Owner only: the server refuses everyone else; this page only hides itself. Every save is followed by a re-read.

const field = 'mt-1 block text-xs font-medium uppercase tracking-wide text-text-muted';

function ProducerRow({ p, onSave }) {
  const [nb, setNb] = useState(shareToPercent(p.new_business_share));
  const [rn, setRn] = useState(shareToPercent(p.renewal_share));
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setNb(shareToPercent(p.new_business_share)); setRn(shareToPercent(p.renewal_share)); }, [p.new_business_share, p.renewal_share]);
  const a = percentToShare(nb);
  const b = percentToShare(rn);
  const changed = a.ok && b.ok && (a.share !== (p.new_business_share ?? null) || b.share !== (p.renewal_share ?? null));
  async function save() {
    setBusy(true);
    try { await onSave(p, { new_business_share: a.share, renewal_share: b.share, reason: reason.trim() || null }); setReason(''); } finally { setBusy(false); }
  }
  return (
    <div data-producer-share={p.user_id} className="rounded-card border border-border bg-surface p-3">
      <div className="flex flex-wrap items-baseline gap-x-3">
        <span className="text-sm font-medium text-text">{p.label || 'unnamed user'}</span>
        <span className="text-xs text-text-dim">{p.role}{p.status !== 'active' ? `, ${p.status}` : ''}</span>
        <span className="ml-auto text-xs text-text-dim">new business {shareWords(p.new_business_share)}, renewal {shareWords(p.renewal_share)}</span>
      </div>
      <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-3">
        <label className={field}>New business %
          <Input aria-label={`New business share for ${p.label}`} inputMode="decimal" value={nb} onChange={(e) => setNb(e.target.value)} placeholder="not stated" className="mt-1" />
        </label>
        <label className={field}>Renewal %
          <Input aria-label={`Renewal share for ${p.label}`} inputMode="decimal" value={rn} onChange={(e) => setRn(e.target.value)} placeholder="not stated" className="mt-1" />
        </label>
        <label className={field}>Reason (kept on the activity line)
          <Input aria-label={`Reason for ${p.label}`} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1" />
        </label>
      </div>
      {(!a.ok || !b.ok) && <p className="mt-1 text-xs text-accent">A share is a percent from 0 to 100, or blank for not stated.</p>}
      <div className="mt-2 flex items-center gap-3">
        <span className="text-xs text-text-dim">{p.updated_at ? `last changed ${formatDateTime(p.updated_at)}` : 'never stated'}</span>
        <Button size="sm" className="ml-auto" disabled={busy || !changed} onClick={save}>{busy ? 'Saving...' : 'Save'}</Button>
      </div>
    </div>
  );
}

function DefaultProducer({ data, producers, onSave }) {
  const def = data || { state: 'absent' };
  const [choice, setChoice] = useState(def.state === 'stated' ? def.user_id : '');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setChoice(def.state === 'stated' ? def.user_id : ''); }, [def.state, def.user_id]);
  const active = producers.filter((p) => p.status === 'active');
  const words = def.state === 'stated' ? (def.label || 'a user not in this list') : (def.state === 'absent' ? 'not set: a new deal with no producer stays unassigned and is noted' : 'not a TRACON user: a new deal with no producer stays unassigned and is noted');
  return (
    <div data-default-producer={def.state} className="rounded-card border border-border bg-surface p-3">
      <div className="text-sm text-text">Default producer: <span className="font-medium">{words}</span></div>
      <p className="mt-1 text-xs text-text-muted">A new deal with no producer stated gets this person. You reassign a deal from its producer key.</p>
      <div className="mt-2 flex items-end gap-2">
        <label className={`${field} flex-1`}>Default producer
          <select aria-label="Default producer" value={choice} onChange={(e) => setChoice(e.target.value)}
            className="mt-1 block min-h-[40px] w-full rounded-card border border-border bg-bg px-3 text-sm text-text">
            <option value="" disabled>Choose a producer</option>
            {active.map((p) => <option key={p.user_id} value={p.user_id}>{p.label}</option>)}
          </select>
        </label>
        <Button size="sm" disabled={busy || !choice || (def.state === 'stated' && choice === def.user_id)} onClick={async () => { setBusy(true); try { await onSave(choice); } finally { setBusy(false); } }}>
          {busy ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}

export default function ProducerSharesAdmin() {
  const me = useMe();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try { setData(await getProducerShares()); setError(''); } catch (e) { setError(e?.body?.message || 'The producer shares could not be read right now. Reload to try again.'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function act(fn, done) {
    let res;
    try { res = await fn(); } catch (e) { toast.error(`Not saved: ${e?.body?.message || e?.message || 'failed'}`); return null; }
    for (const w of (res && res.warnings) || []) toast.info(w);
    try { setData(await getProducerShares()); toast.success(done); } catch { toast.info(`${done} The list could not be read back; reload to check it.`); }
    return res;
  }

  if (me && !ownerPagesVisible(me)) return <div className="p-4 text-sm text-text-muted">Only the owner sees this page.</div>;
  const producers = (data && data.producers) || [];
  return (
    <div className="max-w-3xl p-4">
      <h2 className="text-lg font-semibold text-text">Producer shares</h2>
      <p className="mt-1 text-sm text-text-muted">Each producer's share of the agency's commission, for new business and for renewals. Blank means not stated: the commission view then shows no producer amount for that line. A single policy can carry its own share, set on the deal beside its producer.</p>
      {error && <p className="mt-3 text-sm text-accent">{error}</p>}
      {!data && !error && <p className="mt-3 text-sm text-text-muted">Loading...</p>}
      {data && (
        <div className="mt-4 flex flex-col gap-3">
          <DefaultProducer data={data.default_producer} producers={producers}
            onSave={(id) => act(() => setDefaultProducer(id), 'Default producer saved.')} />
          {producers.map((p) => (
            <ProducerRow key={p.user_id} p={p}
              onSave={(x, body) => act(() => setProducerShares(x.user_id, body), `Shares for ${x.label || 'the producer'} saved.`)} />
          ))}
          {!producers.length && <p className="text-sm text-text-muted">No owners or producers yet.</p>}
        </div>
      )}
    </div>
  );
}
